import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import {
  PageDescription,
  PageTitle,
  SearchCircle,
  Typography,
} from '../components/styled'
import LanguagesDropdown from '../components/languageDropdown'
import FullLogo from '../assets/logos/full-logo.png'
import theme from '../constants/theme'
import apiReferenceRequests from '../services/api/referenceRequests'
import apiReferenceContacts from '../services/api/referenceContacts'
import User from '../assets/svgs/user'
import ReferenceForm from '../components/referenceRequest/ReferenceForm'
import {
  Page,
  TopBar,
  Content,
  Card,
  CardHead,
  CardHeadRow,
  IconBadge,
  Divider,
  InfoBox,
  InfoIcon,
  Grid,
  LoadingWrapper,
} from '../components/referenceRequest/styled'
import ExclamationCircle from '../assets/svgs/exclamationCircle'
import CheckOne from '../assets/svgs/checkOne'

const RESULT_TYPE = { SUCCESS: 'success', ERROR: 'error' }

function ReferenceRequestForm() {
  const { t } = useTranslation()
  const { requestToken: token } = useParams()
  const [candidateInfo, setCandidateInfo] = useState({})
  const [message, setMessage] = useState()
  const [resultType, setResultType] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const isSubmittedOrExpired = resultType !== null

  const RESULT_CONFIG = {
    [RESULT_TYPE.SUCCESS]: {
      icon: <CheckOne width="50px" height="50px" />,
      title: t('reference.formSubmittedTitle'),
      borderColor: theme.success,
    },
    [RESULT_TYPE.ERROR]: {
      icon: (
        <ExclamationCircle
          width="50px"
          height="50px"
          color={theme.talentinoWarning}
        />
      ),
      title: t('dataManagement.somethingWrongMsg'),
      borderColor: theme.talentinoWarning,
    },
  }

  const config = RESULT_CONFIG[resultType] || RESULT_CONFIG[RESULT_TYPE.ERROR]

  const getRequestDetails = () => {
    setIsLoading(true)
    apiReferenceRequests
      .getSingle(token)
      .then((res) => setCandidateInfo(res))
      .catch((err) => {
        if (err?.response?.status === 409) {
          setMessage(t('reference.formAlreadySubmitted'))
        } else if (err?.response?.status === 403) {
          setMessage(t('reference.formPastDeadline'))
        } else {
          setMessage(` ${err?.response?.data?.description ?? ''}`)
        }
        setResultType(RESULT_TYPE.ERROR)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    getRequestDetails()
  }, [token])

  const handleSubmit = (data, setLoading) => {
    apiReferenceContacts
      .submitContacts(token, { contacts: data.references })
      .then((res) => {
        setResultType(RESULT_TYPE.SUCCESS)
        setMessage(res?.msg || t('reference.formSubmittedMessage'))
      })
      .catch((err) => {
        setResultType(RESULT_TYPE.ERROR)
        setMessage(err?.message || err?.response?.data?.description)
      })
      .finally(() => setLoading(false))
  }

  if (isLoading) {
    return (
      <LoadingWrapper>
        <Spinner />
      </LoadingWrapper>
    )
  }

  return (
    <Page>
      <TopBar className="d-flex align-items-center justify-content-between px-3">
        <img width="130px" src={FullLogo} alt="logo" />
        {!isSubmittedOrExpired && <LanguagesDropdown />}
      </TopBar>

      {isSubmittedOrExpired ? (
        <div className="d-flex flex-grow-1 flex-column justify-content-center align-items-center">
          <SearchCircle style={{ borderColor: `${config.borderColor}30` }}>
            {config.icon}
          </SearchCircle>
          <PageTitle>{config.title}</PageTitle>
          <PageDescription>{message}</PageDescription>
        </div>
      ) : (
        <Content>
          <div className="text-center mb-4">
            <Typography
              bold
              size="24px"
              color={theme.brandColorOrange}
              className="d-block mb-2"
            >
              {t('reference.refernceRequestForm')}
            </Typography>
            <Typography size="14px" className="text-muted">
              {t('reference.requestFormDetails')}
            </Typography>
          </div>

          {/* Your Information */}
          <Card>
            <CardHead className="mb-0">
              <CardHeadRow>
                <IconBadge>
                  <User color={theme.brandColorOrange} />
                </IconBadge>
                <Typography size="15px" bold color={theme.gray100}>
                  {t('reference.yourInformation')}
                </Typography>
              </CardHeadRow>
            </CardHead>
            <Divider spaced />
            <Grid columns={3}>
              <div>
                <Typography
                  size="11px"
                  className="text-uppercase d-block"
                  color={theme.talentinoSecondaryGray}
                >
                  {t('reference.candidateName')}
                </Typography>
                <Typography size="14px" bold color={theme.gray100}>
                  {candidateInfo?.candidateName}
                </Typography>
              </div>
              <div>
                <Typography
                  size="11px"
                  className="text-uppercase d-block"
                  color={theme.talentinoSecondaryGray}
                >
                  {t('reference.position')}
                </Typography>
                <Typography size="14px" bold color={theme.gray100}>
                  {candidateInfo?.position}
                </Typography>
              </div>
              <div>
                <Typography
                  size="11px"
                  className="text-uppercase d-block"
                  color={theme.talentinoSecondaryGray}
                >
                  {t('reference.submissionDate')}
                </Typography>
                <Typography size="14px" bold color={theme.gray100}>
                  {candidateInfo?.submissionDate}
                </Typography>
              </div>
            </Grid>
          </Card>

          {/* Instructions */}
          <InfoBox>
            <InfoIcon color={theme.talentinoBlue} />
            <Typography size="13px" color={theme.talentinoTextGray}>
              {t('reference.instructions.description', {
                requiredRefNumber: candidateInfo?.requiredReferences || 1,
              })}
            </Typography>
          </InfoBox>

          <ReferenceForm
            key={`reference-form-${candidateInfo?.requiredReferences || 1}`}
            requiredReferences={candidateInfo?.requiredReferences || 1}
            onSubmit={handleSubmit}
          />
        </Content>
      )}
    </Page>
  )
}

export default ReferenceRequestForm
